import { forwardRef, useMemo } from "react";
import { VisuallyHidden } from "react-aria";
import {
  Select as SelectPrimitive,
  type SelectProps as SelectPrimitiveProps,
  ListBoxSection,
} from "react-aria-components";
import { type PropsWithOptionalTestID } from "@boi/components-core";

import * as S from "./CountrySelect.styled";
import { OuterField } from "../../SelectV2/OuterField";
import { ChevronIcon } from "../../SelectV2/ChevronIcon";
import { Popover } from "../../SelectV2/Popover";
import { ListBoxItem } from "../../SelectV2/ListBoxItem";

type Item = {
  id: string;
  label: string;
  callingCode: string;
  flag: JSX.Element;
  isPromoted?: boolean;
};

export type CountrySelectProps = PropsWithOptionalTestID &
  Omit<SelectPrimitiveProps, "children"> & {
    items: Item[];
    popoverWidth: number;
  };

const CountrySelect = forwardRef<HTMLButtonElement, CountrySelectProps>(
  (props, ref) => {
    const {
      items,
      popoverWidth,
      testID = "country-select",
      ...otherProps
    } = props;

    const selectedItem = items.find(({ id }) => {
      return id === otherProps.selectedKey;
    });

    const promotedItems = useMemo(
      () => items.filter((item) => item.isPromoted),
      [items]
    );

    const standardItems = useMemo(
      () => items.filter((item) => !item.isPromoted),
      [items]
    );

    return (
      <SelectPrimitive {...otherProps} data-testid={testID}>
        <OuterField>
          <S.Button ref={ref}>
            <S.ButtonIconWrapper>
              {selectedItem && (
                <>
                  <div aria-hidden="true">{selectedItem.flag}</div>
                  <VisuallyHidden>{selectedItem.label}</VisuallyHidden>
                </>
              )}
            </S.ButtonIconWrapper>
            <ChevronIcon />
          </S.Button>
        </OuterField>

        <Popover width={popoverWidth}>
          <S.ListBox>
            {promotedItems.length > 0 && (
              <ListBoxSection items={promotedItems}>
                {(item) => (
                  <ListBoxItem
                    id={item.id}
                    icon={item.flag}
                    textValue={`${item.label} ${item.callingCode}`}
                  >
                    {`${item.label} ${item.callingCode}`}
                  </ListBoxItem>
                )}
              </ListBoxSection>
            )}

            {standardItems.length > 0 && (
              <ListBoxSection items={standardItems}>
                {(item) => (
                  <ListBoxItem
                    id={item.id}
                    icon={item.flag}
                    textValue={`${item.label} ${item.callingCode}`}
                  >
                    {`${item.label} ${item.callingCode}`}
                  </ListBoxItem>
                )}
              </ListBoxSection>
            )}
          </S.ListBox>
        </Popover>
      </SelectPrimitive>
    );
  }
);

CountrySelect.displayName = "CountrySelect";

export default CountrySelect;
